// src/components/ui/AuthBranding.tsx — header + footer atoms shared across
// the three auth screens (Login, Register, Forgot-PIN).
//
// BrandMark — the 60x60 brassDk Train tile that anchors each auth screen.
// BackLink — the "← Back to sign in" affordance at the bottom of Register
//   and Forgot.
// SuccessCard — the tone-tinted "you're done" surface that replaces the
//   form on a successful submit. Sage for "all set"; brass for "we'll be
//   in touch". Matches the prototype's two variants.

import type { CSSProperties, ReactNode } from "react";
import { C, F } from "../../theme";
import { Card } from "./Card";
import { Button } from "./Button";
import * as Icons from "./Icon";

// ---------------------------------------------------------------------------
// BrandMark
// ---------------------------------------------------------------------------

const brandMarkStyle: CSSProperties = {
  width: 60,
  height: 60,
  borderRadius: 14,
  background: C.brassDk,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 14,
  boxShadow: "inset 0 1px 0 rgba(244,226,168,0.3), 0 6px 16px rgba(122,84,11,0.35)",
};

export const BrandMark = () => (
  <div style={brandMarkStyle}>
    <Icons.Train s={30} c={C.brassGlow} />
  </div>
);

// ---------------------------------------------------------------------------
// BackLink
// ---------------------------------------------------------------------------

export interface BackLinkProps {
  onClick: () => void;
  label?: string;
}

const backLinkStyle: CSSProperties = {
  background: "none",
  border: "none",
  color: C.inkSoft,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  padding: "12px 0 4px",
  display: "block",
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: 6,
};

export const BackLink = ({ onClick, label = "← Back to sign in" }: BackLinkProps) => (
  <button type="button" onClick={onClick} style={backLinkStyle}>
    {label}
  </button>
);

// ---------------------------------------------------------------------------
// SuccessCard
// ---------------------------------------------------------------------------

export type SuccessCardTone = "sage" | "brass";

export interface SuccessCardProps {
  tone: SuccessCardTone;
  title: string;
  body: ReactNode;
  primaryLabel?: string;
  onPrimary: () => void;
}

const tones: Record<SuccessCardTone, { bg: string; fg: string; bd: string; Icon: typeof Icons.Check }> = {
  sage:  { bg: C.sageBg,  fg: C.sageDk,  bd: C.sageLt,  Icon: Icons.Check },
  brass: { bg: C.brassBg, fg: C.brassDk, bd: C.brassLt, Icon: Icons.Mail  },
};

export const SuccessCard = ({
  tone,
  title,
  body,
  primaryLabel = "Back to sign in",
  onPrimary,
}: SuccessCardProps) => {
  const t = tones[tone];
  const Icon = t.Icon;
  return (
    <Card style={{ width: "100%", maxWidth: 380, textAlign: "center" }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: t.bg,
          border: `1px solid ${t.bd}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 12px",
        }}
      >
        <Icon s={28} c={t.fg} />
      </div>
      <h2
        style={{
          fontFamily: F.serif,
          fontSize: 22,
          fontWeight: 700,
          color: C.ink,
          margin: 0,
          letterSpacing: -0.3,
        }}
      >
        {title}
      </h2>
      <p style={{ fontSize: 14, color: C.inkSoft, fontWeight: 600, marginTop: 8, lineHeight: 1.5 }}>
        {body}
      </p>
      <Button variant="primary" fullWidth onClick={onPrimary} style={{ marginTop: 14 }}>
        {primaryLabel}
      </Button>
    </Card>
  );
};
